import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import MobilePageTopBar, { MOBILE_TOPBAR_CONTROL_H } from '../components/MobilePageTopBar';
import PageSkeleton from '../components/PageSkeleton';
import { useActiveShipments } from '../utils/api-hooks/seaTracking';
import { useActiveFlights, useActiveTrucks } from '../utils/api-hooks/modeTracking';
import { filterModeRows } from '../utils/modeRows';
import { MODE_KEYS, parseModeParam, serializeModeParam, buildWindowSource, countByMode } from '../utils/modeFilter';
import { useWeatherDigestAlerts, useExtremeWeatherAlerts } from '../utils/api-hooks/weatherDigest';
import { useMultiCommodityRegionsData } from '../utils/api-hooks/analytics';
import { convertWeight } from '../utils/groupByVarietyType';
import { parseMapParams, setStatusParam } from '../utils/mapUrlParams';
import useCommodityParam from '../hooks/useCommodityParam';
import { hasCommodity } from '../utils/modeRows';
import useMapParams from '../hooks/useMapParams';
import useMapPartyParams from '../hooks/useMapPartyParams';
import useSeasonBundle from '../hooks/useSeasonBundle';
import { canDownloadSeasonPdf } from '../utils/seasonAccessGate';
import { useBiqPageContext } from '../context/BiqPageContext';
import { filterByDays, windowWeight, windowRows } from '../utils/arrivalWindow';
import {
  shipmentDestOptionKeys,
  shipmentOriginOptionKeys,
  parseCountryParam,
  serializeCountryParam,
  allCountryParam,
  passesCountryFilter,
} from '../utils/countryFilters';
import ScorecardsBar from './components/map/ScorecardsBar';
import MapSidebar from './components/map/MapSidebar';
import UnifiedMapContainer from './components/map/UnifiedMapContainer';
import GlobalFilterStrip from './components/map/GlobalFilterStrip';

// Module-level stable references so memos/effects don't churn on every render
// while userDetails / shipment query data is still loading.
const EMPTY_COMMODITIES = [];
const EMPTY_SHIPMENTS = [];
const EMPTY_OPTIONS = [];
const EMPTY_TOP = [];

const MapPage = () => {
  const { t } = useTranslation();
  const { setPageExtras } = useBiqPageContext() || {};
  const { userDetails, email } = useUser();
  const commodities = userDetails?.preferred_commodities || EMPTY_COMMODITIES;
  // DashboardLayout's mobile drawer toggle, handed down via Outlet context so
  // this page can render its own inline hamburger (mobile only) instead of
  // the standalone header bar — see DashboardLayout's `isMapPage` gate.
  const { onMenuClick } = useOutletContext() || {};

  // Cross-page commodity (URL → Firestore fallback). Same hook the strip uses so
  // both stay in sync. Other map params (layer, status) live in page state only, never in the URL.
  const [searchParams, setSearchParams] = useMapParams();
  const [selectedCommodity] = useCommodityParam();
  const {
    layer: layerMode,
    status: selectedStatuses,
    region: zoomRegion,
    country: zoomCountry,
    panel: deepLinkPanel,
  } = parseMapParams(searchParams);
  const shipmentsView = layerMode === 'shipments';

  // Sticky party chrome keyed by email so account switches do not flash prior names.
  const [partyChrome, setPartyChrome] = useState({
    email: '',
    ready: false,
    importerOptions: EMPTY_OPTIONS,
    exporterOptions: EMPTY_OPTIONS,
  });
  const partyReady = partyChrome.email === email && partyChrome.ready;
  const importerOptions = partyChrome.email === email ? partyChrome.importerOptions : EMPTY_OPTIONS;
  const exporterOptions = partyChrome.email === email ? partyChrome.exporterOptions : EMPTY_OPTIONS;

  const {
    importer,
    exporter,
    setImporter,
    setExporter,
    clearExporterUrl,
  } = useMapPartyParams({
    importerOptions,
    exporterOptions,
    shipmentsView,
  });

  // Shipment data — party filters re-fetch server-side (render-only).
  const { data, isLoading: shipmentsLoading, isError, refetch } = useActiveShipments({
    importer,
    exporter,
    email,
  });
  // Season Summary has its own request: neither call can block the other.
  const {
    data: seasonBundleData,
    isLoading: seasonBundleLoading,
    isError: seasonBundleError,
  } = useSeasonBundle({ email });
  // Season PDF Download follows map_season_access, not the party data (loading/unknown denies).
  const seasonPdfAllowed = canDownloadSeasonPdf(seasonBundleData?.season_access);
  // Stable fallback: inline `|| []` allocates a new array every render while
  // data is undefined, which churns arriving* memos and re-fires setPageExtras
  // (Maximum update depth — loud under local StrictMode / cold load).
  const shipments = data?.data?.shipments ?? EMPTY_SHIPMENTS;
  const topImporters = partyReady ? (data?.data?.top_importers ?? EMPTY_TOP) : null;

  useEffect(() => {
    const payload = data?.data;
    if (!payload) return;
    const ready = Array.isArray(payload.importer_options);
    setPartyChrome({
      email,
      ready,
      importerOptions: ready
        ? payload.importer_options
        : EMPTY_OPTIONS,
      exporterOptions: Array.isArray(payload.exporter_options)
        ? payload.exporter_options
        : EMPTY_OPTIONS,
    });
  }, [data, email]);
  // Exporter is Shipments-only — drop it from the URL when leaving that layer.
  useEffect(() => {
    if (!shipmentsView) clearExporterUrl();
  }, [shipmentsView, clearExporterUrl]);

  const partyFilterProps = {
    partyReady,
    importer,
    importerOptions,
    onImporterChange: setImporter,
    showExporter: shipmentsView,
    exporter,
    exporterOptions,
    onExporterChange: setExporter,
  };
  // Air + truck are map-only layers: they never feed the arrival windows,
  // scorecards or sidebar. A failed/404 endpoint is a quiet empty list.
  const { data: flightsData, isError: flightsError } = useActiveFlights();
  const { data: trucksData, isError: trucksError } = useActiveTrucks();
  // On a failed refetch react-query keeps the old data: show nothing instead.
  const flightsLive = (!flightsError && flightsData?.data?.flights) || EMPTY_SHIPMENTS;
  // DEV-only preview (see src/dev); the guarded import is dropped from builds.
  const [devPreview, setDevPreview] = useState(null);
  useEffect(() => {
    if (import.meta.env.DEV) {
      import('../dev/airPreview').then((m) => setDevPreview(() => m.applyAirPreview));
    }
  }, []);
  const flights = useMemo(() => (devPreview ? devPreview(flightsLive) : flightsLive), [devPreview, flightsLive]);
  const trucks = (!trucksError && trucksData?.data?.trucks) || EMPTY_SHIPMENTS;

  // Fullscreen toggle (mirrors Analytics' ?fullscreen=1). DashboardLayout
  // hides the app sidebar and TopBar when this is set; we hide the
  // ScorecardsBar and MapSidebar here so the map fills the viewport.
  const isFullscreen = searchParams.get('fullscreen') === '1';
  const setFullscreen = useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      if (next) params.set('fullscreen', '1');
      else params.delete('fullscreen');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Weather alerts — driven by the commodity selector. When a commodity is picked,
  // fetch only that one's alerts; otherwise fall back to all preferred.
  const alertCommodities = useMemo(
    () => (selectedCommodity ? [selectedCommodity] : commodities),
    [selectedCommodity, commodities],
  );
  const { alerts: weatherAlerts } = useWeatherDigestAlerts(alertCommodities);
  const { alerts: extremeAlerts } = useExtremeWeatherAlerts(alertCommodities);

  // Harvest (season-shift) alerts — reuse the same cached producing-regions query the
  // map uses; the backend already gates alert.active to in-season/<=8-weeks-before-start.
  // Use the SAME `email` accessor as UnifiedMapContainer so the react-query cache key
  // matches (dedupes the fetch, and keeps hover/click ids in sync with rendered markers).
  const { regions: alertRegions } = useMultiCommodityRegionsData(
    email,
    alertCommodities,
    null,
  );
  const harvestAlerts = useMemo(
    () => (alertRegions || []).filter(
      (r) => r.alert?.active && r.latitude != null && r.longitude != null,
    ),
    [alertRegions],
  );

  const setLayerMode = useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      if (next && next !== 'all') params.set('layer', next);
      else params.delete('layer');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Status filter setter — same URL contract as GlobalFilterStrip's internal
  // handleStatusChange (all-selected or empty both clear the param, since
  // "no status param" already means "show all"). Mobile-only consumer: the
  // "Map controls" panel inside UnifiedMapContainer reuses this so the status
  // filter stays wired to the same URL state whether it's toggled from the
  // top strip (desktop) or from next to the map controls (mobile).
  const setSelectedStatuses = useCallback(
    (next) => setStatusParam(searchParams, setSearchParams, next),
    [searchParams, setSearchParams],
  );

  // UI state (page-local; not part of cross-page URL contract yet)
  const [activePanel, setActivePanel] = useState(null);
  const [weightUnit, setWeightUnit] = useState('boxes');
  const [expandedSection, setExpandedSection] = useState(null);
  const toggleExpandedSection = useCallback(
    (id) => setExpandedSection((prev) => (prev === id ? null : id)),
    [],
  );
  const [selectedShipment, setSelectedShipment] = useState(null);
  // Region hovered in the Harvest Alerts panel → its map pin enlarges.
  const [hoveredRegionId, setHoveredRegionId] = useState(null);
  // Region whose Harvest Alerts card was clicked → map flies to it + opens its tooltip.
  // A per-click seq makes every click a fresh object reference, so re-clicking the SAME
  // card (after manually closing the popup) still re-triggers the fly-to + open.
  const [focusedRegion, setFocusedRegionState] = useState(null);
  const focusSeqRef = useRef(0);
  const setFocusedRegion = useCallback((r) => {
    focusSeqRef.current += 1;
    setFocusedRegionState(r ? { ...r, _seq: focusSeqRef.current } : null);
  }, []);

  // Hydrate activePanel from `?panel` so deep-links (e.g. clicking the Weather
  // Alerts scorecard on Analytics) actually open the matching sidebar on /map.
  // Re-runs on URL change so back/forward + new deep-links also apply.
  useEffect(() => {
    if (deepLinkPanel) setActivePanel(deepLinkPanel);
  }, [deepLinkPanel]);

  // Origin / Destination filters (`origin`, `dest`); memoised on the raw strings.
  const originRaw = searchParams.get('origin');
  const destRaw = searchParams.get('dest');
  const originSel = useMemo(() => parseCountryParam(originRaw), [originRaw]);
  const destSel = useMemo(() => parseCountryParam(destRaw), [destRaw]);

  // Transport modes (`mode`): absent = all three (Sea + Air + Truck).
  const modeRaw = searchParams.get('mode');
  const modeSel = useMemo(() => parseModeParam(modeRaw), [modeRaw]);
  const seaOn = modeSel.keys.has('sea');
  const airOn = modeSel.keys.has('air');
  const truckOn = modeSel.keys.has('truck');
  const selectedModes = useMemo(() => MODE_KEYS.filter((k) => modeSel.keys.has(k)), [modeSel]);

  // Options from unfiltered rows of the selected modes + defaults + URL keys, so a selection never vanishes.
  // Option keys come only from the voyage stops (heads) carrying the selected commodity.
  const optionRows = useMemo(
    () => [
      ...(seaOn ? shipments : EMPTY_SHIPMENTS),
      ...(airOn ? flights : EMPTY_SHIPMENTS),
      ...(truckOn ? trucks : EMPTY_SHIPMENTS),
    ],
    [shipments, flights, trucks, seaOn, airOn, truckOn],
  );
  const originKeys = useMemo(() => {
    const keys = new Set([
      ...(seaOn ? shipments : EMPTY_SHIPMENTS)
        .flatMap((r) => shipmentOriginOptionKeys(r, selectedCommodity)),
      ...[...(airOn ? flights : EMPTY_SHIPMENTS), ...(truckOn ? trucks : EMPTY_SHIPMENTS)]
        .filter((r) => !selectedCommodity || hasCommodity(r, selectedCommodity))
        .map(() => 'PERU'),
    ]);
    originSel.keys.forEach((k) => keys.add(k));
    keys.delete('');
    return [...keys].sort();
  }, [shipments, flights, trucks, seaOn, airOn, truckOn, originSel, selectedCommodity]);
  const destKeys = useMemo(() => {
    const keys = new Set(optionRows.flatMap((r) => shipmentDestOptionKeys(r, selectedCommodity)));
    destSel.keys.forEach((k) => keys.add(k));
    keys.delete('');
    return [...keys].sort();
  }, [optionRows, destSel, selectedCommodity]);

  const setModeKeys = useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      const value = serializeModeParam(next);
      if (value == null) params.delete('mode');
      else params.set('mode', value);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const modeFilter = useMemo(
    () => ({
      keys: MODE_KEYS,
      selectedKeys: selectedModes,
      mode: modeSel.mode,
      onChange: setModeKeys,
      onSelectAll: () => setModeKeys(MODE_KEYS),
    }),
    [selectedModes, modeSel, setModeKeys],
  );
  const setCountryParam = useCallback(
    (name, value) => {
      const params = new URLSearchParams(searchParams);
      if (value == null) params.delete(name);
      else params.set(name, value);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const setOriginKeys = useCallback(
    (next) => setCountryParam('origin', serializeCountryParam(next, originKeys)),
    [setCountryParam, originKeys],
  );
  const setDestKeys = useCallback(
    (next) => setCountryParam('dest', serializeCountryParam(next, destKeys)),
    [setCountryParam, destKeys],
  );
  // Explicit "All" writes the unrestricted state even with no option list loaded.
  const selectAllOrigin = useCallback(() => setCountryParam('origin', allCountryParam()), [setCountryParam]);
  const selectAllDest = useCallback(() => setCountryParam('dest', allCountryParam()), [setCountryParam]);
  const originFilter = useMemo(
    () => ({
      keys: originKeys,
      selectedKeys: originSel.mode === 'all' ? originKeys : [...originSel.keys],
      mode: originSel.mode,
      onChange: setOriginKeys,
      onSelectAll: selectAllOrigin,
    }),
    [originKeys, originSel, setOriginKeys, selectAllOrigin],
  );
  const destFilter = useMemo(
    () => ({
      keys: destKeys,
      selectedKeys: destSel.mode === 'all' ? destKeys : [...destSel.keys],
      mode: destSel.mode,
      onChange: setDestKeys,
      onSelectAll: selectAllDest,
    }),
    [destKeys, destSel, setDestKeys, selectAllDest],
  );
  const countryPredicate = useCallback(
    (s) => passesCountryFilter(s, originSel, destSel),
    [originSel, destSel],
  );

  // Commodity then country filters: single point feeding scorecards and map markers.
  const filteredShipments = useMemo(() => {
    const byCommodity = !selectedCommodity
      ? shipments
      : shipments.filter((s) => {
          if (!s.commodities) return false;
          return s.commodities
            .split(',')
            .some((c) => c.trim().toLowerCase() === selectedCommodity.toLowerCase());
        });
    return byCommodity.filter(countryPredicate);
  }, [shipments, selectedCommodity, countryPredicate]);

  const filteredFlights = useMemo(
    () => filterModeRows(flights, 'air', selectedCommodity, originSel, destSel),
    [flights, selectedCommodity, originSel, destSel],
  );
  const filteredTrucks = useMemo(
    () => filterModeRows(trucks, 'truck', selectedCommodity, originSel, destSel),
    [trucks, selectedCommodity, originSel, destSel],
  );

  // ONE source for the scorecards and sidebar: the selected modes' rows (each
  // already commodity/country filtered). Sea only is the sea array itself.
  const windowSource = useMemo(
    () => buildWindowSource(selectedModes, filteredShipments, filteredFlights, filteredTrucks),
    [selectedModes, filteredShipments, filteredFlights, filteredTrucks],
  );

  // Compute arrivals for 3, 7, and 10 day windows
  // One shared `now` so the three windows are consistent and cumulative.
  const arrivalWindows = useMemo(() => {
    const now = new Date();
    const at = (days) => filterByDays(windowSource, days, now, selectedCommodity);
    return { arriving3: at(3), arriving7: at(7), arriving10: at(10) };
  }, [windowSource, selectedCommodity]);
  const { arriving3, arriving7, arriving10 } = arrivalWindows;

  // Scorecard weights follow the commodity selector, not just the shipment list.
  const weightOf = useCallback(
    (entries) => windowWeight(entries, selectedCommodity),
    [selectedCommodity],
  );

  // Active arrivals panel tracks which time window (3, 7, or 10)
  const arrivingShipments = windowRows(
    activePanel === 'arrivals3' ? arriving3
      : activePanel === 'arrivals10' ? arriving10
      : activePanel === 'arrivals' ? arriving7
      : [],
  );

  const alertsCount = (extremeAlerts?.filter(a =>
      String(a.status).toLowerCase() === 'active' && ['high', 'alto'].includes((a.market_impact || '').toLowerCase())
    ).length || 0)
    + (weatherAlerts?.filter(a => {
      const active = a.is_active;
      if (active === false || active === 'false' || active === 'False' || active === 0) return false;
      return ['high', 'alto'].includes((a.market_impact || '').toLowerCase());
    }).length || 0);

  // Publish map scorecard extras for BiQ. Depend on primitive counts/weights
  // (not arriving* array identities) so a context re-render cannot re-fire this.
  const arriving3Count = arriving3.length;
  const arriving7Count = arriving7.length;
  const arriving10Count = arriving10.length;
  const arriving3Boxes = convertWeight(weightOf(arriving3), 'boxes');
  const arriving7Boxes = convertWeight(weightOf(arriving7), 'boxes');
  const arriving10Boxes = convertWeight(weightOf(arriving10), 'boxes');
  const harvestAlertsCount = harvestAlerts.length;
  const modesKey = selectedModes.join(',');
  const by3 = countByMode(arriving3);
  const by7 = countByMode(arriving7);
  const by10 = countByMode(arriving10);
  const byModeKey = [by3, by7, by10].map((b) => `${b.sea}/${b.air}/${b.truck}`).join('|');

  // Sea off: drop any selected vessel so no sea card or route lingers.
  useEffect(() => {
    if (!seaOn) setSelectedShipment(null);
  }, [seaOn]);

  useEffect(() => {
    if (!setPageExtras) return undefined;
    setPageExtras({
      commodity: selectedCommodity || '',
      layer: layerMode,
      panel: activePanel || '',
      modes: modesKey ? modesKey.split(',') : [],
      arriving: {
        d3: { shipments: arriving3Count, boxes: arriving3Boxes, by_mode: by3 },
        d7: { shipments: arriving7Count, boxes: arriving7Boxes, by_mode: by7 },
        d10: { shipments: arriving10Count, boxes: arriving10Boxes, by_mode: by10 },
      },
      weather_alerts_active: alertsCount,
      harvest_alerts: harvestAlertsCount,
    });
  }, [
    setPageExtras,
    selectedCommodity,
    layerMode,
    activePanel,
    modesKey,
    byModeKey,
    arriving3Count,
    arriving7Count,
    arriving10Count,
    arriving3Boxes,
    arriving7Boxes,
    arriving10Boxes,
    alertsCount,
    harvestAlertsCount,
  ]);

  // Clear extras only on unmount — not on every deps change (that paired with
  // setPageExtras above and amplified update storms under StrictMode).
  useEffect(() => {
    if (!setPageExtras) return undefined;
    return () => setPageExtras(null);
  }, [setPageExtras]);

  if (shipmentsLoading) {
    return <PageSkeleton shape="map" />;
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-500 mb-3">{t('seaTracking.error', 'Error loading data')}</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-bloom-green text-white rounded-lg text-sm hover:bg-[#12b152]"
          >
            {t('common.update', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col max-lg:h-auto lg:h-full max-lg:bg-dark-app ${
        isFullscreen ? 'p-[12px]' : 'max-lg:pb-[88px] lg:px-[32px] lg:pt-[24px] lg:pb-[32px]'
      }`}
    >
      {/* Global filter strip — single source of truth for commodity + status,
          and home for the fullscreen toggle (kept visible in both modes so
          users can exit fullscreen). Sits on the page background, above the
          card, with the fullscreen button pushed to the right edge.
          Mobile (<lg): the drawer hamburger sits on the SAME row, to the
          filters' left, directly on this page's gradient background — no
          separate header bar (DashboardLayout hides its standalone header
          for this route; see `isMapPage` there). Desktop (lg+): the button
          is `lg:hidden`, present in the DOM but never shown, so the row
          collapses back to exactly the original single-child layout. Hidden
          entirely in dashboard fullscreen (`isFullscreen`) since there's no
          drawer to open there. */}
      {isFullscreen ? (
        <div className="flex items-center gap-[8px]">
          <div className="min-w-0 flex-1">
            <GlobalFilterStrip
              isFullscreen={isFullscreen}
              onEnterFullscreen={() => setFullscreen(true)}
              onExitFullscreen={() => setFullscreen(false)}
              {...partyFilterProps}
            />
          </div>
        </div>
      ) : (
        <>
          <MobilePageTopBar onMenuClick={onMenuClick} isFullscreen={false} onEnterFullscreen={() => setFullscreen(true)} onExitFullscreen={() => setFullscreen(false)}>
            <GlobalFilterStrip
              showFullscreen={false}
              commodityVariant="stacked"
              commodityHeightClass={MOBILE_TOPBAR_CONTROL_H}
              {...partyFilterProps}
            />
          </MobilePageTopBar>
          <div className="hidden lg:flex items-center gap-[8px]">
            <div className="min-w-0 flex-1">
              <GlobalFilterStrip
                isFullscreen={false}
                onEnterFullscreen={() => setFullscreen(true)}
                onExitFullscreen={() => setFullscreen(false)}
                {...partyFilterProps}
              />
            </div>
          </div>
        </>
      )}
      {/* 24px between the filter row and the card (tightened in fullscreen so
          the map keeps as much of the viewport as possible). */}
      <div className={`shrink-0 ${isFullscreen ? 'h-[12px]' : 'hidden lg:block h-[24px]'}`} />

      {/* Panel column + map card. The panel sits on the page background beside
          the card (not inside it), so opening one narrows the map rather than
          splitting the card. Page-level chrome stays visible in fullscreen —
          same model as Analytics, which keeps the chart's own scorecards in
          fullscreen and only hides the app sidebar and TopBar (handled in
          DashboardLayout). Mobile (<lg): the page grows to content height and
          the app <main> scrolls; the map itself gets an explicit 70svh height
          (see UnifiedMapContainer) so it can't collapse when the scorecards +
          controls + legend + timeline stack. Desktop (lg+): flex-1/min-h-0 so
          the map fills the remaining viewport. */}
      <div className={`flex gap-[16px] max-lg:block lg:flex-1 lg:min-h-0 ${isFullscreen ? '' : 'max-md:px-[16px] md:max-lg:px-[32px]'}`}>
        <MapSidebar
          mode={activePanel?.startsWith('arrivals') ? 'arrivals' : activePanel}
          timeWindow={activePanel === 'arrivals3' ? 3 : activePanel === 'arrivals10' ? 10 : 7}
          onClose={() => setActivePanel(null)}
          shipments={arrivingShipments}
          weatherAlerts={weatherAlerts || []}
          extremeAlerts={extremeAlerts || []}
          harvestAlerts={harvestAlerts}
          onHoverRegion={setHoveredRegionId}
          onFocusRegion={setFocusedRegion}
          weightUnit={weightUnit}
          selectedCommodity={selectedCommodity}
        />

        <div className="min-w-0 flex flex-col overflow-hidden rounded-[12px] border border-[#E8EBE9] bg-white dark:border-[#30363d] dark:bg-[#1d1d1d] max-lg:w-full lg:flex-1 lg:min-h-0">
          <ScorecardsBar
            arriving3Count={arriving3.length}
            arriving3Weight={weightOf(arriving3)}
            arriving7Count={arriving7.length}
            arriving7Weight={weightOf(arriving7)}
            arriving10Count={arriving10.length}
            arriving10Weight={weightOf(arriving10)}
            alertsCount={alertsCount}
            harvestAlertsCount={harvestAlerts.length}
            activePanel={activePanel}
            onPanelToggle={setActivePanel}
            weightUnit={weightUnit}
            onWeightUnitChange={setWeightUnit}
            expandedSection={expandedSection}
            onExpandedSectionToggle={toggleExpandedSection}
            topImporters={topImporters}
            canDownloadSeasonPdf={seasonPdfAllowed}
            selectedCommodity={selectedCommodity}
            email={email || ''}
            destSel={destSel}
            originSel={originSel}
            seasonBundle={seasonBundleData ?? null}
            seasonBundleLoading={seasonBundleLoading}
            seasonBundleError={seasonBundleError}
          />

          {/* Map container — receives already-commodity-filtered shipments so the
              scorecard counts above and the map markers below stay consistent. */}
          <div className="relative min-w-0 max-lg:flex-none lg:flex-1 lg:min-h-0">
            <UnifiedMapContainer
              shipments={seaOn ? filteredShipments : EMPTY_SHIPMENTS}
              flights={airOn ? filteredFlights : EMPTY_SHIPMENTS}
              trucks={truckOn ? filteredTrucks : EMPTY_SHIPMENTS}
              seaOn={seaOn}
              layerMode={layerMode}
              onLayerModeChange={setLayerMode}
              selectedShipment={selectedShipment}
              onShipmentSelect={setSelectedShipment}
              commodities={commodities}
              hoveredRegionId={hoveredRegionId}
              focusedRegion={focusedRegion}
              selectedCommodity={selectedCommodity}
              selectedStatuses={selectedStatuses}
              onStatusesChange={setSelectedStatuses}
              zoomCountry={zoomCountry}
              zoomRegion={zoomRegion}
              mobileControlsExpanded={expandedSection === 'controls'}
              originFilter={originFilter}
              destFilter={destFilter}
              modeFilter={modeFilter}
              countryPredicate={countryPredicate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapPage;
